import React, { useEffect } from 'react';
import { Box, Typography, type SxProps, type Theme } from '@mui/material';
import { SelectFieldComponent } from '../../../../components/common/SelectFieldComponent';
import { useAsociaciones, useAsociacionesLoading, useAsociacionesError, useFetchAsociaciones } from '../store/asociacionStore';
import { type AsociacionListItem } from '../types/asociacion';

export interface AsociacionSelectProps {
  /** Current selected value */
  value?: number | string;
  /** Change handler */
  onChange: (value: string | number) => void;
  /** Field label */
  label?: string;
  /** Error message */
  error?: string;
  /** Whether the field is disabled */
  disabled?: boolean;
  /** Whether the field is required */
  required?: boolean;
  /** Placeholder text */
  placeholder?: string;
  /** Custom filter function to filter asociaciones */
  filter?: (asociacion: AsociacionListItem) => boolean;
  /** Whether to show procedencia information when an association is selected */
  showProcedencia?: boolean;
  /** Custom sx prop for styling */
  sx?: SxProps<Theme>;
}

/**
 * AsociacionSelect Component
 *
 * A reusable dropdown component that displays asociaciones from the backend.
 * Features:
 * - Automatic data fetching using AsociacionService
 * - Loading states and error handling
 * - Filtering capabilities
 * - Optional procedencia display when association is selected
 * - Full TypeScript support
 * - Consistent styling with other form components
 */
export const AsociacionSelect: React.FC<AsociacionSelectProps> = ({
  value,
  onChange,
  label = 'Asociación',
  error,
  disabled = false,
  required = false,
  placeholder,
  filter,
  showProcedencia = false,
  sx,
}) => {
  const asociaciones = useAsociaciones();
  const loading = useAsociacionesLoading();
  const fetchError = useAsociacionesError();
  const fetchAsociaciones = useFetchAsociaciones();

  // Fetch asociaciones on component mount
  useEffect(() => {
    fetchAsociaciones();
  }, [fetchAsociaciones]);

  // Filter asociaciones based on props
  const filteredAsociaciones = React.useMemo(() => {
    let filtered = asociaciones;

    // Apply custom filter if provided
    if (filter) {
      filtered = filtered.filter(filter);
    }

    return filtered;
  }, [asociaciones, filter]);

  // Convert asociaciones to select options
  const options = React.useMemo(() => {
    return filteredAsociaciones.map((asociacion) => ({
      value: asociacion.idAsociacion,
      label: asociacion.descripcion,
    }));
  }, [filteredAsociaciones]);

  // Find selected association to display procedencia
  const selectedAsociacion = React.useMemo(() => {
    if (!value || !filteredAsociaciones.length) return null;
    return filteredAsociaciones.find(asociacion => asociacion.idAsociacion === Number(value));
  }, [value, filteredAsociaciones]);

  // Handle error state
  const displayError = error || (fetchError ? 'Error cargando asociaciones' : undefined);

  return (
    <Box sx={sx}>
      <SelectFieldComponent
        label={required ? `${label} *` : label}
        value={value || ''}
        onChange={onChange}
        options={options}
        error={displayError}
        disabled={disabled || loading}
        placeholder={placeholder}
        loading={loading}
      />

      {/* Display procedencia if enabled and association is selected */}
      {showProcedencia && selectedAsociacion && selectedAsociacion.procedencia && (
        <Box sx={{ mt: 1.5 }}>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              fontWeight: 500,
              mb: 0.5,
              fontSize: '0.875rem'
            }}
          >
            Procedencia:
          </Typography>
          <Typography
            variant="body1"
            sx={{
              fontWeight: 600,
              color: 'primary.main',
              fontSize: '1rem',
              padding: '8px 12px',
              backgroundColor: 'primary.50',
              border: '1px solid',
              borderColor: 'primary.200',
              borderRadius: 1,
              display: 'inline-block'
            }}
          >
            {selectedAsociacion.procedencia}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default AsociacionSelect;